import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowUpDown, Circle, Clock, Flame, Hand, KanbanSquare, List } from "lucide-react";
import { toast } from "sonner";

import { deleteTask, fetchClinicTasks, updateTask } from "@/lib/api/tasks";
import { ApiError } from "@/lib/api/client";
import { getAuthUser } from "@/lib/api/token-store";
import { subscribeUserThreadEvents } from "@/lib/realtime/chat-realtime-service";
import { requestOpenThreadMessage } from "@/lib/notifications/notification-navigation";
import { getThreadGroupsConfig, subscribeThreadGroups } from "@/lib/stores/thread-groups-store";
import {
  getTaskCounts,
  refreshTaskCounts,
  subscribeTaskCounts,
} from "@/lib/stores/task-counts-store";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SmartViewsSidebar, TASK_SMART_VIEWS } from "@/components/pages/tasks/smart-views-sidebar";
import { TaskRow } from "@/components/pages/tasks/task-row";
import { TaskBoard } from "@/components/pages/tasks/task-board";
import { SegmentedToggle } from "@/components/pages/calendar/facet-filter";
import { useHasPermission } from "@/hooks/use-permissions";
import { useUserPreferences } from "@/hooks/use-user-preferences";
import { setUserPreferences } from "@/lib/stores/user-preference-store";
import { cn } from "@/lib/utils";

const SORTS = [
  { id: "priority", label: "Priority" },
  { id: "newest", label: "Newest" },
];

function taskDueMs(task) {
  if (!task?.dueAt) return null;
  const t = new Date(task.dueAt).getTime();
  return Number.isNaN(t) ? null : t;
}

/** A task counts as "done today" only if it was completed on the current calendar day. */
function isDoneToday(task, nowMs) {
  if (task?.status !== "done" || !task.doneAt) return false;
  const d = new Date(task.doneAt);
  const n = new Date(nowMs);
  return (
    d.getFullYear() === n.getFullYear() &&
    d.getMonth() === n.getMonth() &&
    d.getDate() === n.getDate()
  );
}

/**
 * The team Tasks roll-up (design 04-tasks-page): every follow-up across the clinic. Smart
 * views + clinic/teammate breakdowns on the left; the claimable queue on the right, grouped
 * "Overdue" (rose) then "Waiting to be picked up".
 */
export default function TasksPage() {
  const me = String(getAuthUser()?.id || "").trim();
  const [selection, setSelection] = useState({ kind: "view", id: "unclaimed" });
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sort, setSort] = useState("priority");
  const [counts, setCounts] = useState(() => getTaskCounts());
  const [groups, setGroups] = useState(() => getThreadGroupsConfig());
  const [nowMs, setNowMs] = useState(() => Date.now());
  const refreshTimerRef = useRef(null);
  const fetchSeqRef = useRef(0);
  const tasksViewMode = useUserPreferences().tasksViewMode;
  const isBoard = tasksViewMode === "board";
  const setTasksViewMode = useCallback((mode) => setUserPreferences({ tasksViewMode: mode }), []);

  useEffect(() => subscribeTaskCounts(setCounts), []);
  useEffect(() => subscribeThreadGroups(setGroups), []);
  useEffect(() => {
    // Re-seed immediately: state survives an Activity freeze, so on thaw the snooze/overdue
    // classification must not run against the freeze-time clock for up to 30s.
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // One clinic-wide working set powers every view and count, so the page never disagrees with
  // itself (the server's per-view "snoozed" doesn't match our future-due-date model). Smart views
  // become client-side filters over this set; the teammate ids come from the counts store.
  const load = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const teammateIds = (getTaskCounts()?.teammates || [])
        .map((t) => String(t?.id || "").trim())
        .filter(Boolean);
      const res = await fetchClinicTasks({ assigneeIds: teammateIds });
      if (fetchSeqRef.current !== seq) return;
      setAllTasks(res.tasks);
      setError("");
    } catch (e) {
      if (fetchSeqRef.current !== seq) return;
      setAllTasks([]);
      setError(e instanceof Error ? e.message : "Could not load tasks");
    } finally {
      if (fetchSeqRef.current === seq) setLoading(false);
    }
  }, []);
  const loadRef = useRef(load);
  loadRef.current = load;

  // Reload on mount and whenever the teammate roster changes (so each person's tasks are pulled).
  const teammateKey = (counts?.teammates || [])
    .map((t) => String(t?.id || "").trim())
    .filter(Boolean)
    .sort()
    .join(",");
  useEffect(() => {
    void load();
    void refreshTaskCounts();
  }, [teammateKey, load]);

  // Realtime: task events refresh the working set + counts (debounced).
  useEffect(() => {
    return subscribeUserThreadEvents((detail) => {
      if (!String(detail?.action || "").startsWith("task")) return;
      if (refreshTimerRef.current) return;
      refreshTimerRef.current = setTimeout(() => {
        refreshTimerRef.current = null;
        void loadRef.current();
        void refreshTaskCounts();
      }, 600);
    });
  }, []);
  useEffect(
    () => () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    },
    [],
  );

  // Each smart view is a client-side slice of the working set; "snoozed" = an open task whose
  // due date is still in the future, matching the board and the on-screen grouping.
  const viewTasks = useMemo(() => {
    const open = (t) => t.status !== "done";
    if (selection.kind === "teammate") {
      return allTasks.filter((t) => open(t) && String(t.assigneeId || "").trim() === selection.id);
    }
    switch (selection.id) {
      case "unclaimed":
        return allTasks.filter((t) => open(t) && !String(t.assigneeId || "").trim());
      case "mine":
        return allTasks.filter((t) => open(t) && String(t.assigneeId || "").trim() === me);
      case "overdue":
        return allTasks.filter((t) => {
          const due = taskDueMs(t);
          return open(t) && due != null && due <= nowMs;
        });
      case "snoozed":
        return allTasks.filter((t) => {
          const due = taskDueMs(t);
          return open(t) && due != null && due > nowMs;
        });
      case "doneToday":
        return allTasks.filter((t) => isDoneToday(t, nowMs));
      default:
        return allTasks;
    }
  }, [allTasks, selection, me, nowMs]);

  // Sidebar counts + the People roster, derived from the same set so they always agree with it.
  const derivedCounts = useMemo(() => {
    const nameFallback = new Map();
    for (const t of counts?.teammates || []) {
      const id = String(t?.id || "").trim();
      if (id) nameFallback.set(id, String(t?.displayName || "").trim());
    }
    let unclaimed = 0;
    let mine = 0;
    let overdue = 0;
    let snoozed = 0;
    let doneToday = 0;
    const teammateMap = new Map();
    for (const t of allTasks) {
      if (t.status === "done") {
        if (isDoneToday(t, nowMs)) doneToday += 1;
        continue;
      }
      const assignee = String(t.assigneeId || "").trim();
      const due = taskDueMs(t);
      if (!assignee) unclaimed += 1;
      if (assignee === me) mine += 1;
      if (due != null && due <= nowMs) overdue += 1;
      else if (due != null && due > nowMs) snoozed += 1;
      if (assignee) {
        const entry = teammateMap.get(assignee) || {
          id: assignee,
          displayName: String(t.assignee?.displayName || "").trim() || nameFallback.get(assignee) || "",
          count: 0,
        };
        if (!entry.displayName) {
          entry.displayName = String(t.assignee?.displayName || "").trim() || nameFallback.get(assignee) || "";
        }
        entry.count += 1;
        teammateMap.set(assignee, entry);
      }
    }
    const meEntry = teammateMap.get(me);
    if (meEntry && !meEntry.displayName) {
      meEntry.displayName = nameFallback.get(me) || String(getAuthUser()?.displayName || "").trim() || "You";
    }
    const teammates = [...teammateMap.values()].sort((a, b) =>
      (a.displayName || "").localeCompare(b.displayName || ""),
    );
    return { unclaimed, mine, overdue, snoozed, doneToday, teammates };
  }, [allTasks, counts, me, nowMs]);

  const sortedTasks = useMemo(() => {
    const list = [...viewTasks];
    if (sort === "newest") {
      list.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
    } else {
      // Priority: overdue first (oldest due first), then upcoming dues, then no-due by age.
      list.sort((a, b) => {
        const dueA = taskDueMs(a);
        const dueB = taskDueMs(b);
        if (dueA != null && dueB != null) return dueA - dueB;
        if (dueA != null) return -1;
        if (dueB != null) return 1;
        return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
      });
    }
    return list;
  }, [viewTasks, sort]);

  const { overdueTasks, snoozedTasks, waitingTasks, doneTasks } = useMemo(() => {
    const overdue = [];
    const snoozed = [];
    const waiting = [];
    const doneList = [];
    for (const task of sortedTasks) {
      if (task.status === "done") {
        doneList.push(task);
        continue;
      }
      const due = taskDueMs(task);
      // A future due date means the task is snoozed until then; a past one is overdue; no due
      // date means it's just waiting in the queue.
      if (due == null) waiting.push(task);
      else if (due <= nowMs) overdue.push(task);
      else snoozed.push(task);
    }
    return { overdueTasks: overdue, snoozedTasks: snoozed, waitingTasks: waiting, doneTasks: doneList };
  }, [sortedTasks, nowMs]);

  const handleToggle = useCallback(async (taskId, nextDone) => {
    setAllTasks((prev) =>
      prev.map((t) =>
        t.id === taskId
          ? { ...t, status: nextDone ? "done" : "todo", doneAt: nextDone ? new Date().toISOString() : null }
          : t,
      ),
    );
    try {
      await updateTask({ taskId, status: nextDone ? "done" : "todo" });
      void refreshTaskCounts();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not update task");
      void loadRef.current();
    }
  }, []);

  const claimTask = useCallback(async (taskId) => {
    if (!me) return;
    setAllTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, assigneeId: me } : t)));
    try {
      const res = await updateTask({ taskId, assigneeId: me });
      if (res.task) {
        setAllTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...res.task, thread: t.thread } : t)));
      }
      void refreshTaskCounts();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast.error("Someone else just claimed this task.");
      } else {
        toast.error(e instanceof Error ? e.message : "Could not claim task");
      }
      void loadRef.current();
    }
  }, []);

  // Delete a task permanently (any source, any status).
  const removeTask = useCallback(async (taskId) => {
    setAllTasks((prev) => prev.filter((t) => t.id !== taskId));
    try {
      await deleteTask({ taskId });
      void refreshTaskCounts();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not delete task");
      void loadRef.current();
    }
  }, []);

  // Take over a task someone else holds (deliberate reassignment — bypasses the claim guard).
  const takeOverTask = useCallback(async (taskId) => {
    if (!me) return;
    setAllTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, assigneeId: me, assignee: null } : t)));
    try {
      const res = await updateTask({ taskId, assigneeId: me, reassign: true });
      if (res.task) {
        setAllTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...res.task, thread: t.thread } : t)));
      }
      void refreshTaskCounts();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not take over task");
      void loadRef.current();
    }
  }, []);

  // Clear a task's assignee — it returns to the unclaimed queue.
  const releaseTask = useCallback(async (taskId) => {
    setAllTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, assigneeId: "", assignee: null } : t)),
    );
    try {
      await updateTask({ taskId, assigneeId: "" });
      void refreshTaskCounts();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not clear assignee");
      void loadRef.current();
    }
  }, []);

  // "Claim next": grab the top unclaimed task of the current queue.
  const claimNext = useCallback(() => {
    const next = [...overdueTasks, ...waitingTasks].find((t) => !String(t.assigneeId || "").trim());
    if (!next) {
      toast.info("Nothing unclaimed in this view.");
      return;
    }
    void claimTask(next.id);
  }, [overdueTasks, waitingTasks, claimTask]);

  const openThread = useCallback((threadId) => {
    // App.jsx listens on this bridge and switches to Messages; the messages hook opens the thread.
    requestOpenThreadMessage({ threadId });
  }, []);

  // RBAC: tasks:view renders the queue read-only — TaskRow hides its action buttons when the
  // mutation handlers are absent, and the board ignores drops. One gate, no per-button audit.
  const canManage = useHasPermission("tasks", "manage");
  const rowActions = canManage
    ? { onToggle: handleToggle, onClaim: claimTask, onTakeOver: takeOverTask, onRelease: releaseTask, onDelete: removeTask }
    : {};
  const doneRowActions = canManage ? { onToggle: handleToggle, onDelete: removeTask } : {};

  const activeView = selection.kind === "view" ? TASK_SMART_VIEWS.find((v) => v.id === selection.id) : null;
  const headerTitle =
    selection.kind === "view" ? activeView?.label || "Tasks" : selection.name || "Tasks";
  const HeaderIcon = activeView?.icon || Circle;
  const totalShown =
    overdueTasks.length + snoozedTasks.length + waitingTasks.length + doneTasks.length;

  const renderGroupHeading = (icon, label, count, toneClassName, ruleClassName) => (
    <div className="mb-2 flex items-center gap-2">
      {icon}
      <p className={cn("text-[11px] font-bold uppercase tracking-wider", toneClassName)}>
        {label} · {count}
      </p>
      <span className={cn("h-px flex-1", ruleClassName)} aria-hidden />
    </div>
  );

  return (
    <div className="flex h-full min-h-0">
      {!isBoard ? (
        <SmartViewsSidebar counts={derivedCounts} selection={selection} onSelect={setSelection} />
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-[56px] shrink-0 items-center gap-3 border-b border-border px-5">
          {isBoard ? (
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <KanbanSquare className="h-4 w-4" strokeWidth={2.25} aria-hidden />
              </span>
              <h2 className="truncate text-[15px] font-bold text-foreground">Task board</h2>
            </div>
          ) : (
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <HeaderIcon className="h-4 w-4" strokeWidth={2.25} aria-hidden />
              </span>
              <h2 className="truncate text-[15px] font-bold text-foreground">{headerTitle}</h2>
              <span className="shrink-0 rounded-full border border-border bg-muted/60 px-2 py-[3px] text-[11px] font-medium leading-none text-muted-foreground">
                {totalShown} task{totalShown === 1 ? "" : "s"}
              </span>
            </div>
          )}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {!isBoard ? (
              <>
                <button
                  type="button"
                  onClick={() => setSort((prev) => (prev === "priority" ? "newest" : "priority"))}
                  title="Toggle sort order"
                  className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs font-medium text-muted-foreground shadow-sm transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <ArrowUpDown className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
                  {SORTS.find((s) => s.id === sort)?.label}
                </button>
                {/* <button
                  type="button"
                  onClick={claimNext}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md bg-indigo-600 px-3 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:bg-indigo-600 dark:hover:bg-indigo-500"
                >
                  <Hand className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
                  Claim next
                </button> */}
              </>
            ) : null}
            <SegmentedToggle
              value={tasksViewMode}
              onChange={setTasksViewMode}
              ariaLabel="Tasks view"
              size="sm"
              segments={[
                { id: "list", label: "List", icon: List, title: "Task list" },
                { id: "board", label: "Board", icon: KanbanSquare, title: "Swim-lane board — lanes by teammate" },
              ]}
            />
          </div>
        </div>

        {isBoard ? (
          <TaskBoard groups={groups} canManage={canManage} />
        ) : (
        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-1.5 px-5 py-4">
            {error ? <p className="text-xs text-destructive">{error}</p> : null}
            {loading && allTasks.length === 0 ? (
              <div className="space-y-1.5">
                <Skeleton className="h-14 w-full rounded-lg" />
                <Skeleton className="h-14 w-full rounded-lg" />
                <Skeleton className="h-14 w-full rounded-lg" />
              </div>
            ) : totalShown === 0 ? (
              <div className="flex flex-col items-center gap-1 px-4 py-16 text-center">
                <p className="text-sm font-medium text-foreground">Queue&apos;s clear</p>
                <p className="text-xs text-muted-foreground">
                  Tasks appear when the bot pauses a conversation or a teammate adds a follow-up.
                </p>
              </div>
            ) : (
              <>
                {overdueTasks.length > 0 ? (
                  <div className="pb-3">
                    {renderGroupHeading(
                      <Flame className="h-3.5 w-3.5 shrink-0 text-rose-500 dark:text-rose-400" strokeWidth={2.5} aria-hidden />,
                      "Overdue",
                      overdueTasks.length,
                      "text-rose-600 dark:text-rose-400",
                      "bg-rose-100 dark:bg-rose-500/20",
                    )}
                    <div className="space-y-1.5">
                      {overdueTasks.map((task) => (
                        <TaskRow
                          key={task.id}
                          task={task}
                          nowMs={nowMs}
                          groups={groups}
                          {...rowActions}
                          onOpenThread={openThread}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
                {waitingTasks.length > 0 ? (
                  <div className="pb-3">
                    {renderGroupHeading(
                      <Circle className="h-3 w-3 shrink-0 text-muted-foreground" strokeWidth={2.5} aria-hidden />,
                      "Waiting to be picked up",
                      waitingTasks.length,
                      "text-muted-foreground",
                      "bg-border/70",
                    )}
                    <div className="space-y-1.5">
                      {waitingTasks.map((task) => (
                        <TaskRow
                          key={task.id}
                          task={task}
                          nowMs={nowMs}
                          groups={groups}
                          {...rowActions}
                          onOpenThread={openThread}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
                {snoozedTasks.length > 0 ? (
                  <div className="pb-3">
                    {renderGroupHeading(
                      <Clock className="h-3.5 w-3.5 shrink-0 text-amber-500 dark:text-amber-400" strokeWidth={2.5} aria-hidden />,
                      "Snoozed",
                      snoozedTasks.length,
                      "text-amber-600 dark:text-amber-400",
                      "bg-amber-100 dark:bg-amber-500/20",
                    )}
                    <div className="space-y-1.5">
                      {snoozedTasks.map((task) => (
                        <TaskRow
                          key={task.id}
                          task={task}
                          nowMs={nowMs}
                          groups={groups}
                          {...rowActions}
                          onOpenThread={openThread}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
                {doneTasks.length > 0 ? (
                  <div className="pb-3">
                    {renderGroupHeading(
                      <Circle className="h-3 w-3 shrink-0 text-emerald-500 dark:text-emerald-400" strokeWidth={2.5} aria-hidden />,
                      "Done",
                      doneTasks.length,
                      "text-emerald-600 dark:text-emerald-400",
                      "bg-emerald-100 dark:bg-emerald-500/20",
                    )}
                    <div className="space-y-1.5">
                      {doneTasks.map((task) => (
                        <TaskRow
                          key={task.id}
                          task={task}
                          nowMs={nowMs}
                          groups={groups}
                          {...doneRowActions}
                          onOpenThread={openThread}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </ScrollArea>
        )}
      </div>
    </div>
  );
}
